import { useState, useEffect, useCallback } from 'react';

declare global {
  interface Window {
    HubSpotConversations?: {
      widget: {
        load: (options?: { email?: string; name?: string }) => void;
        open: () => void;
        close: () => void;
        remove: () => void;
        refresh: () => void;
        status: () => { loaded: boolean };
      };
      /** Clears the visitor's chat cookies; resetWidget reloads the widget. */
      clear?: (opts?: { resetWidget?: boolean }) => void;
      on: (event: string, callback: (payload: any) => void) => void;
      off: (event: string, callback: (payload: any) => void) => void;
    };
    hsConversationsOnReady?: Array<() => void>;
    hsConversationsSettings?: {
      loadImmediately?: boolean;
      identificationEmail?: string;
      identificationToken?: string;
    };
  }
}

export const useHubspotChat = () => {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [activeConversation, setActiveConversation] = useState<string | null>(null);

  useEffect(() => {
    // Set up ready callback
    window.hsConversationsOnReady = window.hsConversationsOnReady || [];
    window.hsConversationsOnReady.push(() => {
      console.log('[HUBSPOT] Chat widget loaded');
      setHasLoaded(true);
    });

    // Check if already loaded
    if (window.HubSpotConversations?.widget?.status?.()?.loaded) {
      setHasLoaded(true);
    }

    return () => {
      window.hsConversationsOnReady = [];
    };
  }, []);

  // Subscribe to conversation events
  useEffect(() => {
    if (!hasLoaded || !window.HubSpotConversations) return;

    const handleConversationStarted = (payload: any) => {
      console.log('[HUBSPOT] Conversation started:', payload);
      setActiveConversation(payload?.conversation?.conversationId || null);
    };

    const handleConversationClosed = () => {
      console.log('[HUBSPOT] Conversation closed');
      setActiveConversation(null);
    };

    window.HubSpotConversations.on('conversationStarted', handleConversationStarted);
    window.HubSpotConversations.on('conversationClosed', handleConversationClosed);

    return () => {
      if (window.HubSpotConversations) {
        window.HubSpotConversations.off('conversationStarted', handleConversationStarted);
        window.HubSpotConversations.off('conversationClosed', handleConversationClosed);
      }
    };
  }, [hasLoaded]);

  const openWidget = useCallback(() => {
    if (hasLoaded && window.HubSpotConversations?.widget) {
      window.HubSpotConversations.widget.open();
    }
  }, [hasLoaded]);

  const closeWidget = useCallback(() => {
    if (hasLoaded && window.HubSpotConversations?.widget) {
      window.HubSpotConversations.widget.close();
    }
  }, [hasLoaded]);

  const refreshWidget = useCallback(() => {
    if (hasLoaded && window.HubSpotConversations?.widget) {
      window.HubSpotConversations.widget.refresh();
    }
  }, [hasLoaded]);

  const identifyUser = useCallback((email: string, name?: string) => {
    if (hasLoaded && window.HubSpotConversations?.widget) {
      window.HubSpotConversations.widget.load({ email, name });
      console.log('[HUBSPOT] User identified:', { email, name });
    }
  }, [hasLoaded]);

  return {
    hasLoaded,
    activeConversation,
    openWidget,
    closeWidget,
    refreshWidget,
    identifyUser,
  };
};
